import { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';

import { Modal, Button, Input } from '@core/components/ui';
import { validateFolderName } from '../utils/folderValidation';

/**
 * RenameFolderModal Component
 *
 * Modal dialog for renaming existing folders.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {Function} props.onClose - Callback to close modal
 * @param {Function} props.onRename - Callback to rename folder (receives folder name)
 * @param {string} props.currentName - Current folder name
 * @param {boolean} [props.isRenaming=false] - Whether rename is in progress
 */
const RenameFolderModal = ({ isOpen, onClose, onRename, currentName, isRenaming = false }) => {
  const { t } = useTranslation();
  const [folderName, setFolderName] = useState('');
  const [error, setError] = useState('');

  // Initialize with current name when modal opens
  useEffect(() => {
    if (isOpen && currentName) {
      setFolderName(currentName);
    }
  }, [isOpen, currentName]);

  // Handle rename
  const handleRename = useCallback(async () => {
    const validationError = validateFolderName(folderName, t);

    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      await onRename(folderName.trim());
      // On success, reset and close
      setFolderName('');
      setError('');
    } catch (err) {
      // Handle RTK Query errors and other errors
      const errorMessage = err?.data?.error || err?.message || t('files.folders.updateError', 'Error al renombrar la carpeta');
      setError(errorMessage);
    }
  }, [folderName, onRename, validateFolderName, t]);

  // Handle close
  const handleClose = useCallback(() => {
    setFolderName('');
    setError('');
    onClose();
  }, [onClose]);

  // Handle key down
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !isRenaming) {
      e.preventDefault();
      handleRename();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleClose();
    }
  }, [handleRename, handleClose, isRenaming]);

  // Handle input change
  const handleInputChange = (value) => {
    setFolderName(value);
    // Clear error when user starts typing
    setError('');
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('files.explorer.renameFolderTitle', 'Renombrar carpeta')}
      size="sm"
      closeButton
      footer={
        <>
          <Button
            variant="ghost"
            onClick={handleClose}
            disabled={isRenaming}
            className="cursor-pointer"
          >
            {t('common.cancel', 'Cancelar')}
          </Button>
          <Button
            variant="primary"
            onClick={handleRename}
            disabled={isRenaming || !folderName.trim()}
            className="cursor-pointer"
          >
            {isRenaming ? (
              <>
                <Icon icon="eos-icons:loading" className="w-5 h-5 mr-2" />
                {t('common.renaming', 'Renombrando...')}
              </>
            ) : (
              <>
                <Icon icon="mdi:check" className="w-5 h-5 mr-2" />
                {t('files.explorer.rename', 'Renombrar')}
              </>
            )}
          </Button>
        </>
      }
    >
      <Input
        label={t('files.explorer.folderNameLabel', 'Nombre de la carpeta')}
        value={folderName}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        error={error}
        disabled={isRenaming}
        autoFocus
      />
    </Modal>
  );
};

RenameFolderModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onRename: PropTypes.func.isRequired,
  currentName: PropTypes.string,
  isRenaming: PropTypes.bool,
};

export { RenameFolderModal };
